import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { listScopeParams } from '@/services/core/context'
import { mapNetworkRef, type NetworkRef } from '@/services/core/networkRef'
import { searchParams } from '@/services/core/listSearch'
import { sortParams, type ListSort } from '@/services/core/paging'
import type { BiddingEventTarget } from '@/services/lineItems/lineItemsApi'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'
import { mapRateLimits, type RateLimit } from '@/services/earnings/rateLimits'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface CurrencyAmount {
  currency: string
  amount: number
}

export interface OrderBudget {
  startDay: string
  endDay: string
  amount?: CurrencyAmount
  adjustedAmount?: CurrencyAmount
  spend?: CurrencyAmount
}

export interface Order {
  id: string
  name: string
  objectState: string
  /** Owning network — only populated by list queries that span networks. */
  network?: NetworkRef
  advertiserId: string
  advertiserName: string
  teamId: string
  teamName: string
  executionState: string
  externalReference: string
  updateTime: string
  createTime: string
  budget: OrderBudget[]
  budgetRollover: boolean
  /** Paces the order's line items evenly across the budget period. */
  smoothed: boolean
  rateLimits: RateLimit[]
  salespersonUserId?: string
  adOpsUserId?: string
  impressionDelivery?: number
  clickDelivery?: number
  viewableDelivery?: number
  renderedDelivery?: number
  spendDelivery?: CurrencyAmount
  /** Marketplace only: the event the order's line items compete on cost for. */
  optimiseFor?: BiddingEventTarget
  validationWarnings: ValidationWarning[]
}

export interface OrderInput {
  name: string
  objectState?: string
  advertiser?: { id: string }
  team?: { id: string }
  externalReference?: string
  budget?: Array<{ startDay: string; endDay: string; amount?: CurrencyAmount }>
  budgetRollover?: boolean
  smoothed?: boolean
  rateLimits?: RateLimit[]
  salespersonUser?: { id: string } | null
  adOpsUser?: { id: string } | null
  /** `{ eventType: null }` stops optimising, leaving line-item budgets in control. */
  optimiseFor?: BiddingEventTarget | { eventType: null }
}

/** One order of a bulk save: the id, and only the fields being changed. */
export type OrderBulkInput = Partial<OrderInput> & { id: string }

function mapOrder(r: Record<string, unknown>): Order {
  return {
    network: mapNetworkRef(r.network),
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    advertiserId: ((r.advertiser as Record<string, unknown>)?.id as string) ?? '',
    advertiserName: ((r.advertiser as Record<string, unknown>)?.name as string) ?? '',
    teamId: ((r.team as Record<string, unknown>)?.id as string) ?? '',
    teamName: ((r.team as Record<string, unknown>)?.name as string) ?? '',
    executionState: (r.executionState as string) ?? '',
    externalReference: (r.externalReference as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
    createTime: (r.createTime as string) ?? '',
    budget: (r.budget as OrderBudget[]) ?? [],
    budgetRollover: (r.budgetRollover as boolean) ?? false,
    smoothed: (r.smoothed as boolean) ?? false,
    rateLimits: mapRateLimits(r.rateLimits),
    salespersonUserId: ((r.salespersonUser as Record<string, unknown>)?.id as string) || undefined,
    adOpsUserId: ((r.adOpsUser as Record<string, unknown>)?.id as string) || undefined,
    impressionDelivery: (r.impressionDelivery as number) ?? undefined,
    clickDelivery: (r.clickDelivery as number) ?? undefined,
    viewableDelivery: (r.viewableDelivery as number) ?? undefined,
    renderedDelivery: (r.renderedDelivery as number) ?? undefined,
    spendDelivery: r.spendDelivery as Order['spendDelivery'] ?? undefined,
    optimiseFor: (r.optimiseFor as BiddingEventTarget) ?? undefined,
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export interface OrderPage {
  results: Order[]
  totalCount: number
  page: number
  pageSize: number
}

export const OrdersApi = {
  getPage: async (
    page = 1,
    pageSize = 25,
    includeInactive = false,
    search?: string,
    sort?: ListSort | null
  ): Promise<OrderPage> => {
    const params = new URLSearchParams({
      ...searchParams('orders', search),
      ...listScopeParams(),
      concise: 'true',
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: String(includeInactive),
      ...sortParams(sort, {
        orderBy: 'name',
        orderByDirection: 'ASCENDING',
        nullsOrderPolicy: 'FIRST',
      }),
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/orders?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch orders (${res.status})`)
    const data = await res.json()
    const results: Order[] = (Array.isArray(data.results) ? data.results : []).map(mapOrder)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /** @deprecated use getPage */
  getAll: async (): Promise<Order[]> => {
    const page = await OrdersApi.getPage(1, 100)
    return page.results
  },

  getByAdvertiser: async (advertiserId: string): Promise<Order[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      advertiser: advertiserId,
      includeActive: 'true',
      includeInactive: 'true',
      pageSize: '100',
      page: '1',
      orderBy: 'name',
    })
    const res = await apiFetch(`${API_BASE}/orders?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch orders (${res.status})`)
    const data = await res.json()
    const results: Order[] = (Array.isArray(data.results) ? data.results : []).map(mapOrder)
    return results
  },

  /**
   * Fetches several orders in one request. The API's `id` parameter is
   * semicolon-separated and cannot be combined with the advertiser filter, so
   * this is a plain lookup by id.
   */
  getByIds: async (ids: string[]): Promise<Order[]> => {
    if (ids.length === 0) return []
    const params = new URLSearchParams({
      ...listScopeParams(),
      concise: 'true',
      id: ids.join(';'),
      pageSize: String(ids.length),
    })
    const res = await apiFetch(`${API_BASE}/orders?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch orders (${res.status})`)
    return toArray(await res.json()).map(mapOrder)
  },

  /**
   * Copies an order, with as much or as little of what hangs off it as asked
   * for. The copy's id is generated by the caller, as everywhere else in the
   * app, so it can navigate to the copy without waiting on the response.
   *
   * `clearBudgets` is the inverse of keeping the budgets, matching the API.
   */
  copy: async (
    id: string,
    input: {
      newId: string
      name: string
      advertiserId?: string
      /** The state the copied line items start in; empty keeps their own. */
      lineItemUserState?: string
      copyLineItems?: boolean
      copyTargeting?: boolean
      copyCreatives?: boolean
      copyBudgets?: boolean
      copyReportSchedules?: boolean
    }
  ): Promise<Order> => {
    const params = new URLSearchParams({
      context: getContext(),
      orderId: input.newId,
      name: input.name,
      copyLineItems: String(input.copyLineItems ?? true),
      copyTargeting: String(input.copyTargeting ?? true),
      copyCreatives: String(input.copyCreatives ?? true),
      copyReportSchedules: String(input.copyReportSchedules ?? true),
      clearBudgets: String(!(input.copyBudgets ?? true)),
      ...(input.advertiserId ? { advertiserId: input.advertiserId } : {}),
      ...(input.lineItemUserState ? { lineItemUserState: input.lineItemUserState } : {}),
    })
    const res = await apiFetch(`${API_BASE}/orders/${id}/copy?${params}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: '{}',
    })
    if (!res.ok) await throwApiError(res, 'Failed to copy order')
    return mapOrder(await res.json())
  },

  getById: async (id: string): Promise<Order> => {
    const res = await apiFetch(`${API_BASE}/orders/${id}?context=${getContext()}&showBudgetSpend=true`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch order (${res.status})`)
    return mapOrder(await res.json())
  },

  update: async (id: string, input: OrderInput): Promise<Order> => {
    const res = await apiFetch(`${API_BASE}/orders/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save order')
    return mapOrder(await res.json())
  },

  /**
   * Saves several orders in one request. Each payload carries only the fields
   * it changes; the server leaves the rest alone and applies the whole list in
   * one transaction.
   */
  updateMany: async (inputs: OrderBulkInput[]): Promise<Order[]> => {
    const res = await apiFetch(`${API_BASE}/orders?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(inputs),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save orders')
    return toArray(await res.json()).map(mapOrder)
  },
}
